import { Box, Chip, Tooltip } from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
    DeliveryTrip,
    DeliveryTripEditChange,
    DeliveryTripEditEvent,
    ProductOrder,
} from "../../../../data/entities";
import { formatVietnamTime } from "../../../../utils/dateUtils";
import { DELIVERY_TRIP_STATUS, ROLE } from "../../../../data/enums";

dayjs.extend(utc);

const fieldLabels: Record<DeliveryTripEditChange["field"], string> = {
    vehicle_plate: "Biển số xe",
    trip_number: "Chuyến",
    notes: "Ghi chú",
    scheduled_at: "Ngày giao",
    delivery_address: "Địa chỉ giao hàng",
    quantity: "Số lượng giao",
    delivery_allocations: "Phân bổ địa chỉ giao",
};

export const formatHistoryValue = (
    field: DeliveryTripEditChange["field"],
    value: DeliveryTripEditChange["old_value"]
) => {
    if (Array.isArray(value)) {
        return value.map(row => `${row.delivery_address}: ${row.quantity}`).join("; ") || "—";
    }
    if (value === null || value === "") {
        return field === "vehicle_plate"
            ? "Chưa có xe"
            : field === "delivery_address"
              ? "Chưa có địa chỉ"
              : field === "scheduled_at"
                ? "Chưa có ngày giao"
                : "—";
    }
    if (field === "scheduled_at") {
        const date = dayjs.utc(value);
        return date.isValid() ? date.format("DD/MM/YYYY") : "—";
    }
    return String(value);
};

const editorLabel = (event: DeliveryTripEditEvent) => {
    if (!event.changed_by) return "Không rõ người sửa";
    const { display_name, role } = event.changed_by;
    // const roleLabel = role === ROLE.LOGISTIC_MANAGER || role === ROLE.PRODUCTION_STAFF
    //     ? "Hậu cần điều phối"
    //     : EmployeeRolesDropdown.find((option) => option.value === role)?.text;

    return display_name ? `${display_name}` : "Không rõ người sửa";
};

export const quantityName = (change: DeliveryTripEditChange, order?: ProductOrder) => {
    const rawItems: any[] = (order as any)?.items || [];
    const raw = rawItems.find((item) => String(item.id) === change.sale_order_item_id);
    if (raw?.product?.name || raw?.material?.name) return raw.product?.name || raw.material?.name;
    const normalized: any[] = [...(order?.products || []), ...(order?.materials || [])];
    return (
        normalized.find((item) => String(item.line_item_id) === change.sale_order_item_id)?.name ||
        "Sản phẩm không còn trong đơn"
    );
};

export default function DeliveryTripHistoryBadge({
    trip,
    order,
}: {
    trip: DeliveryTrip;
    order?: ProductOrder;
}) {
    const history = [...(trip.edit_history || [])].sort(
        (a, b) => Date.parse(b.changed_at) - Date.parse(a.changed_at) || b.id.localeCompare(a.id)
    );
    const visible =
        trip.show_edited_badge === true &&
        history.length > 0 &&
        !trip.is_exported &&
        ![
            DELIVERY_TRIP_STATUS.IN_TRANSIT,
            DELIVERY_TRIP_STATUS.DELIVERED,
            DELIVERY_TRIP_STATUS.RETURNED,
        ].includes(trip.status as DELIVERY_TRIP_STATUS);

    if (!visible) return null;

    return (
        <Tooltip
            placement="bottom-start"
            describeChild
            disableTouchListener
            componentsProps={{
                tooltip: {
                    sx: {
                        width: 360,
                        maxWidth: "calc(100vw - 24px)",
                        maxHeight: 320,
                        overflowY: "auto",
                        border: "1px solid #E0E0E0",
                        borderRadius: "10px",
                        backgroundColor: "white",
                        padding: "10px 12px",
                        color: "#292B30",
                        fontSize: 12,
                        lineHeight: "18px",
                        overflowWrap: "anywhere",
                    },
                },
            }}
            title={
                <>
                    <Box sx={{ fontWeight: 600, marginBottom: 1 }}>
                        Đã sửa {trip.edit_count ?? history.length} lần
                    </Box>
                    {history.map((event, eventIndex) => (
                        <Box
                            key={event.id}
                            sx={{
                                borderTop: eventIndex ? "1px solid #EEEEEE" : undefined,
                                marginTop: eventIndex ? 1 : 0,
                                paddingTop: eventIndex ? 1 : 0,
                            }}
                        >
                            {event.changes.map((change, index) => (
                                <div
                                    key={`${change.field}-${change.delivery_trip_item_id || index}`}
                                >
                                    <Box
                                        component="span"
                                        sx={{ fontWeight: 600, color: "#555961", marginRight: 1 }}
                                    >
                                        {fieldLabels[change.field]}
                                        {change.field === "quantity"
                                            ? ` — ${quantityName(change, order)}`
                                            : ""}
                                    </Box>
                                    <span>
                                        {formatHistoryValue(change.field, change.old_value)} →{" "}
                                        {formatHistoryValue(change.field, change.new_value)}
                                    </span>
                                </div>
                            ))}
                            <Box sx={{ color: "#64676E", fontSize: 11 }}>
                                {editorLabel(event)} · {formatVietnamTime(event.changed_at)}
                            </Box>
                        </Box>
                    ))}
                </>
            }
        >
            <Chip
                component="span"
                label="Đã sửa"
                size="small"
                variant="outlined"
                tabIndex={0}
                sx={{
                    display: "flex",
                    width: "fit-content",
                    marginTop: 0.5,
                    height: 18,
                    borderColor: "#D8B85A",
                    borderRadius: "4px",
                    backgroundColor: "#FFF4CC",
                    color: "#795A00",
                    fontSize: 11,
                    cursor: "pointer",
                }}
            />
        </Tooltip>
    );
}
